import type { IEquipoAscensor } from '../../../modelos/CotizadorNBS/entidades/IEquipoAscensor';
import type { IEspecificacionesAscensor } from '../../../modelos/CotizadorNBS/entidades/IEspecificacionesAscensor';
import {
    especificacionesDesdeApi,
    validarEquipo,
    type ICampoDato,
    type ISeccionEspecificaciones,
    type ValorEspecificacion
} from './camposEspecificacion';

/**
 * Pestañas de especificaciones del ascensor (lo que sale en la tabla de la Oferta Comercial). La vista y la
 * validación usan esta misma lista. Todos los campos son obligatorios (cliente, 2026-10-15). Sala de máquinas y
 * doble acceso son Sí/No; maniobra y apertura, listas del catálogo; las entradas traseras solo aplican con doble acceso
 * (cliente, 2026-10-27). No hay más reglas (rangos, relación con las paradas) hasta que el cliente las defina.
 */
export const SECCIONES_ESPECIFICACIONES_ASCENSOR: ISeccionEspecificaciones<IEspecificacionesAscensor>[] = [
    {
        // Ordenado para que cada fila de 3 agrupe lo relacionado: entradas, cabina, puertas y ducto juntos.
        // En el PDF: cabina y puertas como "ancho X fondo/alto".
        clave: 'especificaciones',
        titulo: 'Especificaciones Técnicas',
        campos: [
            { campo: 'SalaMaquinas', etiqueta: 'Sala de máquinas', tipo: 'switch' },
            { campo: 'Maniobra', etiqueta: 'Maniobra', tipo: 'lista', lista: 'maniobras' },
            { campo: 'Alimentacion', etiqueta: 'Alimentación', tipo: 'texto' },
            { campo: 'EntradasPrincipales', etiqueta: 'Entradas frontales', tipo: 'entero' },
            { campo: 'DobleAcceso', etiqueta: 'Doble acceso', tipo: 'switch' },
            { campo: 'EntradasTraseras', etiqueta: 'Entradas traseras', tipo: 'entero', dependeDe: 'DobleAcceso' },
            { campo: 'CabinaAnchoMm', etiqueta: 'Ancho de cabina', tipo: 'mm' },
            { campo: 'CabinaFondoMm', etiqueta: 'Fondo de cabina', tipo: 'mm' },
            { campo: 'CabinaAlturaMm', etiqueta: 'Altura de cabina', tipo: 'mm' },
            { campo: 'PuertaAnchoMm', etiqueta: 'Ancho de puertas', tipo: 'mm' },
            { campo: 'PuertaAltoMm', etiqueta: 'Alto de puertas', tipo: 'mm' },
            { campo: 'Apertura', etiqueta: 'Apertura de puertas', tipo: 'lista', lista: 'aperturas' },
            { campo: 'DuctoAnchoMm', etiqueta: 'Ancho de ducto', tipo: 'mm' },
            { campo: 'DuctoFondoMm', etiqueta: 'Fondo de ducto', tipo: 'mm' },
            { campo: 'FosoMm', etiqueta: 'Foso negativo', tipo: 'mm' },
            { campo: 'SobreRecorridoMm', etiqueta: 'Sobre recorrido', tipo: 'mm' },
            { campo: 'RecorridoMm', etiqueta: 'Recorrido', tipo: 'mm' }
        ]
    },
    {
        clave: 'acabados',
        titulo: 'Acabados',
        campos: [
            { campo: 'Paredes', etiqueta: 'Paredes', tipo: 'acabado' },
            { campo: 'Techo', etiqueta: 'Techo', tipo: 'acabado' },
            { campo: 'Piso', etiqueta: 'Piso', tipo: 'acabado' },
            { campo: 'Pasamanos', etiqueta: 'Pasamanos', tipo: 'acabado' },
            { campo: 'Zocalo', etiqueta: 'Zócalo', tipo: 'acabado' },
            { campo: 'PuertasCabina', etiqueta: 'Puertas de cabina', tipo: 'acabado' },
            { campo: 'PuertasPisos', etiqueta: 'Puertas de pisos', tipo: 'acabado' }
        ]
    }
];


export const estadoInicialEspecificacionesAscensor: IEspecificacionesAscensor = {
    SalaMaquinas: false, RecorridoMm: null, EntradasPrincipales: null, DobleAcceso: false, EntradasTraseras: null, Maniobra: '', Alimentacion: '',
    CabinaAnchoMm: null, CabinaFondoMm: null, CabinaAlturaMm: null, PuertaAnchoMm: null, PuertaAltoMm: null, Apertura: '',
    DuctoAnchoMm: null, DuctoFondoMm: null, FosoMm: null, SobreRecorridoMm: null,
    Paredes: '', Techo: '', Piso: '', Pasamanos: '', Zocalo: '', PuertasCabina: '', PuertasPisos: ''
};

/** Campos de la pestaña "Datos del equipo" del ascensor. Todos obligatorios. */
const CAMPOS_DATOS_ASCENSOR: ICampoDato<IEquipoAscensor>[] = [
    { campo: 'Nombre', etiqueta: 'Nombre' }, { campo: 'Cantidad', etiqueta: 'Cantidad' }, { campo: 'CostoFCA', etiqueta: 'Costo Equipo', admiteCero: true },
    { campo: 'Producto', etiqueta: 'Modelo' }, { campo: 'Maquina', etiqueta: 'Máquina' }, { campo: 'OperadorPuerta', etiqueta: 'Operador de puerta' },
    { campo: 'Tipo', etiqueta: 'Tipo' }, { campo: 'Uso', etiqueta: 'Uso' }, { campo: 'Capacidad', etiqueta: 'Capacidad' },
    { campo: 'VelocidadIngreso', etiqueta: 'Velocidad' }, { campo: 'Paradas', etiqueta: 'Paradas' }, { campo: 'PotenciaMotor', etiqueta: 'Potencia Motor' }
];

/** Campos obligatorios del ascensor sin diligenciar, separados por pestaña. */
export const validarAscensor = (form: Partial<IEquipoAscensor>) =>
    validarEquipo(form, CAMPOS_DATOS_ASCENSOR, SECCIONES_ESPECIFICACIONES_ASCENSOR,
        form.Especificaciones ?? estadoInicialEspecificacionesAscensor);

/** "630 kg · 8 pasajeros"; sin pasajeros (capacidad anterior a la lista) solo los kg. */
export const textoCapacidadAscensor = (capacidad?: number, pasajeros?: number | null) =>
    !capacidad ? '-' : pasajeros ? `${capacidad} kg · ${pasajeros} pasajeros` : `${capacidad} kg`;

/** Especificaciones del ascensor que vienen del back (textos en null pasan a vacío). */
export const especificacionesAscensorDesdeApi = (api?: Partial<Record<keyof IEspecificacionesAscensor, ValorEspecificacion>>) =>
    especificacionesDesdeApi(SECCIONES_ESPECIFICACIONES_ASCENSOR, estadoInicialEspecificacionesAscensor, api);
